import { createRequest, ValidationError } from '@shared/services';

export async function getProduct(params?: GetProductParams) {
    const request = createRequest<ProductsResponse>({
        url: '/product',
        method: 'GET',
        withAuthorization: true,
        params,
    });

    return request.execute();
}

export async function getProductCountByStatus() {
    const request = createRequest<GetProductCountByStatusResponse>({
        url: '/product/count-by-status',
        method: 'GET',
        withAuthorization: true,
    });

    return request.execute();
}

export async function getProductById(productId: string) {
    const request = createRequest<ProductResponse>({
        url: `/product/${productId}`,
        method: 'GET',
        withAuthorization: true,
        data: {
            id: productId,
        },
    });

    return request.execute();
}

export type ProductErrors = {
    data?: Product[];
    errors: ProductErrorFields;
};

export async function getProductRecentlySpec(productId: string) {
    const request = createRequest<GetProductRecentlySpecResponse>({
        url: `/product/recently-spec`,
        method: 'GET',
        withAuthorization: true,
        params: {
            product_id: productId,
        },
    });

    return request.execute();
}

export async function postProduct(payload: ProductPayload) {
    let result: ProductResponse | null = null;
    let errors: ProductErrorFields | null = null;

    try {
        const request = createRequest<ProductResponse>({
            url: '/product',
            method: 'POST',
            data: payload,
            withAuthorization: true,
        });

        result = await request.execute();
    } catch (error) {
        if (error instanceof ValidationError) {
            const details = error.context.detail;

            errors = {} as ProductErrorFields;

            if (Array.isArray(details)) {
                for (let i = 0; i < details.length; i++) {
                    const errorField = details[i];
                    const [, ...field] = errorField.loc;

                    const lastField = field.pop()!;

                    errors[lastField] = errorField.msg;
                }
            }
        }
    }

    if (errors) {
        return { errors } as ProductErrors;
    }

    return result;
}

export async function postProductId(productId: string) {
    const request = createRequest<BasicSuccessResponse>({
        url: '/product/id',
        method: 'POST',
        withAuthorization: true,
        data: {
            id: productId,
        },
    });

    return request.execute();
}

export async function postProductCloneId(productId: string) {
    const request = createRequest<BasicSuccessResponse>({
        url: `/product/clone/${productId}`,
        method: 'POST',
        withAuthorization: true,
        data: {
            id: productId,
        },
    });

    return request.execute();
}

export async function postProductSpecId(productId: string, productSpecId: string) {
    const request = createRequest<BasicSuccessResponse>({
        url: `/product/${productId}/spec-meta-id`,
        method: 'POST',
        withAuthorization: true,
        data: {
            id: productSpecId,
        },
    });

    return request.execute();
}

export async function putProductById(productId: string, payload: ProductPayload) {
    let result: ProductResponse | null = null;
    let errors: ProductErrorFields | null = null;

    try {
        const request = createRequest<ProductResponse>({
            url: `/product/${productId}`,
            method: 'PUT',
            data: payload,
            withAuthorization: true,
        });

        result = await request.execute();
    } catch (error) {
        if (error instanceof ValidationError) {
            const details = error.context.detail;

            errors = {} as ProductErrorFields;

            if (Array.isArray(details)) {
                for (let i = 0; i < details.length; i++) {
                    const errorField = details[i];
                    const [, ...field] = errorField.loc;

                    const lastField = field.pop()!;

                    errors[lastField] = errorField.msg;
                }
            }
        }
    }

    if (errors) {
        return { errors } as ProductErrors;
    }

    return result;
}

export async function putProductBatchUpdate(payload: ProductBatchUpdatePayload[]) {
    const request = createRequest<BasicSuccessResponse>({
        url: '/product/batch-update',
        method: 'PUT',
        data: payload,
        withAuthorization: true,
    });

    return request.execute();
}

export async function deleteProductById(productId: string) {
    const request = createRequest<BasicSuccessResponse>({
        url: `/product/${productId}`,
        method: 'DELETE',
        data: {
            id: productId,
        },
        withAuthorization: true,
    });

    return request.execute();
}
